import React, { useState, useEffect } from "react";
import BaseModal from "components/BaseModal";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

export default function MainOperatorRestrictionFormModal({
  show,
  onClose,
  mode,
  userData,
  onSuccess,
}) {
  const [errors, setErrors]         = useState({});
  const [providerList, setProviderList] = useState([]);
  const [isActive, setIsActive]     = useState(false); // ✅ controlled toggle state
  const [editData, setEditData]     = useState(null);
  const { userid } = useSession();

  // ── Fetch provider dropdown ────────────────────────────────────────────────
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const provForm = new FormData();
        provForm.append("action", "GET");
        provForm.append("userid", userid);
        const provRes = await fetch("https://pro.mokshiq.in/provider_manager_action", {
          method : "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          body   : provForm,
        });
        const provData = await provRes.json();
        setProviderList(provData.data || []);
      } catch (err) {
        console.error("Error fetching dropdowns:", err);
      }
    };
    fetchDropdowns();
  }, [userid]);

  // ── Fetch existing record on edit ──────────────────────────────────────────
  useEffect(() => {
    if (mode === "edit" && userData?.id) {
      const fetchById = async () => {
        try {
          const form = new FormData();
          form.append("action", "GETBYID");
          form.append("id",     userData.id);
          form.append("userid", userid);
          const res    = await fetch("https://pro.mokshiq.in/main_operator_action", {
            method : "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            body   : form,
          });
          const result = await res.json();
          if (result.status) {
            setEditData(result.data);
            setIsActive(result.data.is_active ?? false); // ✅ seed toggle from fetched data
          }
        } catch (err) {
          console.error("Error fetching record:", err);
        }
      };
      fetchById();
    } else {
      setEditData(null);
      setIsActive(false); // ✅ reset to false for add mode
    }
  }, [mode, userData]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    const provider_id = document.getElementById("provider_id")?.value.trim();
    if (!provider_id) newErrors.provider_id = "Provider is required.";
    // min/max are optional (NULL = block entirely), so no required validation
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validateForm()) {
      showMainToast("Please correct the errors before saving.", "danger");
      return;
    }

    const formData = new FormData();
    formData.append("action",      "ADDUpdate");
    formData.append("id",          userData?.id || "");
    formData.append("provider_id", document.getElementById("provider_id").value);
    formData.append("min_amt",     document.getElementById("min_amt").value.trim());
    formData.append("max_amt",     document.getElementById("max_amt").value.trim());
    formData.append("status",   isActive ? "True" : "False"); // ✅ send bool as string

    try {
      const response = await fetch("https://pro.mokshiq.in/main_operator_action", {
        method : "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body   : formData,
      });
      const result = await response.json();
      if (result.status) {
        showMainToast(result.data || "Success!", "success");
        onClose();
        if (onSuccess) onSuccess();
      } else {
        showMainToast(result.data || "Something went wrong!", "danger");
      }
    } catch {
      showMainToast("Something went wrong!", "danger");
    }
  };

  const isReady = mode === "add" || (mode === "edit" && editData !== null);

  return (
    <BaseModal
      show={show}
      onClose={onClose}
      title={
        mode === "edit"
          ? "Edit Main Operator Restriction"
          : "Add Main Operator Restriction"
      }
      footerButtons={[
        { label: "Cancel",                              className: "btn-secondary", dismiss: true },
        { label: mode === "edit" ? "Update" : "Save",  className: "btn-primary",   onClick: handleSave },
      ]}
    >
      <div className="card-body">
        {!isReady ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <form key={editData?.id || "new"} className="row g-3 app-form rounded-control">

            {/* Provider */}
            <div className="col-md-6">
              <label className="form-label">Provider</label>
              <select
                id="provider_id"
                className={`form-select ${errors.provider_id ? "is-invalid" : ""}`}
                defaultValue={editData?.provider_id || ""}
              >
                <option value="">-- Select Provider --</option>
                {providerList.map((p) => (
                  <option key={p.id} value={p.id}>{p.Name}</option>
                ))}
              </select>
              {errors.provider_id && (
                <div className="text-danger small">{errors.provider_id}</div>
              )}
            </div>

            {/* Is Active toggle */}
            <div className="col-md-6 d-flex align-items-center gap-3 pt-4">
              <label className="form-label mb-0">Active</label>
              {/* ✅ Controlled toggle — synced with isActive state */}
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="is_active"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="is_active">
                  {isActive ? (
                    <span className="badge bg-success">Active</span>
                  ) : (
                    <span className="badge bg-secondary">Inactive</span>
                  )}
                </label>
              </div>
            </div>

            {/* Min Amount */}
            <div className="col-md-6">
              <label className="form-label">
                Min Amount
                <span className="text-muted small ms-1">(leave blank = no minimum)</span>
              </label>
              <input
                id="min_amt"
                type="number"
                min="0"
                className="form-control"
                defaultValue={editData?.min_amt || ""}
                placeholder="e.g. 10"
              />
            </div>

            {/* Max Amount */}
            <div className="col-md-6">
              <label className="form-label">
                Max Amount
                <span className="text-muted small ms-1">(leave blank = no maximum)</span>
              </label>
              <input
                id="max_amt"
                type="number"
                min="0"
                className="form-control"
                defaultValue={editData?.max_amt || ""}
                placeholder="e.g. 5000"
              />
            </div>

            {/* Info box: NULL behaviour */}
            <div className="col-12">
              <div className="alert alert-info py-2 mb-0 small">
                <strong>Note:</strong> If both Min and Max are left blank, the operator
                will be <strong>blocked entirely</strong> when this restriction is active.
                Otherwise, only recharges outside the specified range are blocked.
              </div>
            </div>

          </form>
        )}
      </div>
    </BaseModal>
  );
}